import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FaArrowLeft, FaEnvelope, FaPhone, FaCalendar, FaMapMarkerAlt, FaUsers, FaCopy, FaExternalLinkAlt, FaFilePdf, FaUpload, FaCheck } from 'react-icons/fa';
import { anfrageService } from '../services/api';
import { ANTWORT_VORLAGEN, antwortMail, angebotMail, vertragMail, dankeMail, SERVICE_LABEL, datumLang, datumKurz } from '../utils/mailVorlagen';
import { statusKlasse } from './AnfragenList';
import '../styles/Anfragen.css';

const STANDARD_EQUIPMENT = 'Die komplette Sound- und Lichttechnik wird von Happybeats mitgebracht und bereitgestellt.';
const ALT_EQUIPMENT = 'Die komplette Sound- und Lichttechnik bringt Happybeats mit.';
const STANDARD_SPIELZEIT_HOCHZEIT = 'Hintergrundmusik mit Playlist von 18:30 bis 22:00 Uhr, DJ-Set von 22:00 bis 03:00 Uhr.';

// Für Mailprogramme mit Formatierung (Apple Mail, Outlook, Webmailer): jede Zeile als eigene Zeile,
// Leerzeilen bleiben erhalten, Links werden klickbar. Reiner Text geht zusätzlich mit.
function alsHtml(text) {
  const esc = (t) => t.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const link = (t) => t.replace(/(https?:\/\/[^\s<]+)/g, '<a href="$1">$1</a>');
  return '<div>' + String(text).split('\n').map(z => (z.trim() ? link(esc(z)) : '') + '<br>').join('') + '</div>';
}

async function kopieren(text) {
  try {
    if (window.ClipboardItem && navigator.clipboard?.write && /\n/.test(text)) {
      await navigator.clipboard.write([new window.ClipboardItem({
        'text/plain': new Blob([text], { type: 'text/plain' }),
        'text/html': new Blob([alsHtml(text)], { type: 'text/html' })
      })]);
      return true;
    }
    await navigator.clipboard.writeText(text); return true;
  }
  catch (e) {
    const t = document.createElement('textarea'); t.value = text; document.body.appendChild(t); t.select();
    const ok = document.execCommand('copy'); document.body.removeChild(t); return ok;
  }
}

function MailBox({ mail, setMail, an, hinweis }) {
  const [kopiert, setKopiert] = useState('');
  const kopie = async (was, text) => { if (await kopieren(text)) { setKopiert(was); setTimeout(() => setKopiert(''), 1600); } };
  const mailto = `mailto:${encodeURIComponent(an)}?subject=${encodeURIComponent(mail.subject)}&body=${encodeURIComponent(mail.body)}`;
  return (
    <div className="mailbox">
      <div className="form-group">
        <label>Betreff</label>
        <input className="form-input" value={mail.subject} onChange={(e) => setMail({ ...mail, subject: e.target.value })} />
      </div>
      <div className="form-group">
        <label>Text</label>
        <textarea className="form-textarea mail-text" value={mail.body} onChange={(e) => setMail({ ...mail, body: e.target.value })} />
      </div>
      {hinweis && <p className="text-gray klein mb-2">{hinweis}</p>}
      <div className="button-row">
        <button className="btn btn-primary" onClick={() => kopie('text', mail.body)}>{kopiert === 'text' ? <><FaCheck /> Kopiert</> : <><FaCopy /> Text kopieren</>}</button>
        <button className="btn btn-secondary" onClick={() => kopie('betreff', mail.subject)}>{kopiert === 'betreff' ? <><FaCheck /> Kopiert</> : <><FaCopy /> Betreff</>}</button>
        <button className="btn btn-secondary" onClick={() => kopie('email', an)}>{kopiert === 'email' ? <><FaCheck /> Kopiert</> : <><FaCopy /> E-Mail-Adresse</>}</button>
        <a className="btn btn-secondary" href={mailto}><FaExternalLinkAlt /> Im Mailprogramm öffnen</a>
      </div>
    </div>
  );
}

function Dateien({ liste }) {
  if (!liste?.length) return null;
  return (
    <ul className="dateiliste">
      {liste.map(f => <li key={f.id}><FaFilePdf /> <a href={f.url} target="_blank" rel="noreferrer">{f.filename}</a></li>)}
    </ul>
  );
}

function UploadButton({ label, onFile, busy }) {
  const ref = useRef();
  return (
    <>
      <input ref={ref} type="file" accept="application/pdf" hidden onChange={(e) => { if (e.target.files[0]) onFile(e.target.files[0]); e.target.value = ''; }} />
      <button className="btn btn-secondary" disabled={busy} onClick={() => ref.current.click()}><FaUpload /> {busy ? 'Lädt hoch …' : label}</button>
    </>
  );
}

function vertragVorbelegen(a) {
  const v = a.vertragsdaten;
  if (v) return v.equipment === ALT_EQUIPMENT ? { ...v, equipment: STANDARD_EQUIPMENT } : v;
  const hochzeit = a.service === 'hochzeits-dj' || /hochzeit/i.test(a.eventart || '');
  return {
    typ: a.service === 'livemusik' ? 'livemusik' : 'dj',
    auftraggeber: { firma: '', name: `${a.vorname} ${a.nachname}`.trim(), strasse: '', plzOrt: '', telefon: a.telefon, email: a.email },
    veranstaltung: { art: a.eventart, datum: datumKurz(a.datum), ort: a.ort, anschrift: '', gaeste: a.gaeste ? `ca. ${a.gaeste}` : '', aufbau: '', spielzeit: hochzeit && a.service !== 'livemusik' ? STANDARD_SPIELZEIT_HOCHZEIT : '' },
    equipment: STANDARD_EQUIPMENT,
    wuensche: '',
    gage: a.betrag ?? '',
    zusatzstundePreis: a.service === 'livemusik' ? '' : 140,
    zusatzvereinbarungen: ''
  };
}

function AnfrageDetail() {
  const { id } = useParams();
  const [a, setA] = useState(null);
  const [statusListe, setStatusListe] = useState([]);
  const [fehler, setFehler] = useState('');
  const [tab, setTab] = useState('antwort');
  const [vorlage, setVorlage] = useState('videocall');
  const [antwort, setAntwort] = useState({ subject: '', body: '' });
  const [angebot, setAngebot] = useState({ subject: '', body: '' });
  const [vMail, setVMail] = useState({ subject: '', body: '' });
  const [dMail, setDMail] = useState({ subject: '', body: '' });
  const [notizen, setNotizen] = useState('');
  const [betrag, setBetrag] = useState('');
  const [vertrag, setVertrag] = useState(null);
  const [busy, setBusy] = useState('');
  const [meldung, setMeldung] = useState('');
  const [vorschau, setVorschau] = useState('');
  const [buchung, setBuchung] = useState(null);

  const laden = (daten) => {
    setA(daten.anfrage);
    if (daten.status) setStatusListe(daten.status);
    setNotizen(daten.anfrage.notizen || '');
    setBetrag(daten.anfrage.betrag ?? '');
  };

  useEffect(() => {
    anfrageService.getById(id).then((d) => {
      laden(d);
      const s = d.anfrage.status;
      const vorbei = d.anfrage.datum && new Date(d.anfrage.datum) < new Date(new Date().toDateString());
      setTab(s === 'Gebucht' && vorbei ? 'danke' : ['Angenommen', 'Vertrag verschickt', 'Gebucht'].includes(s) ? 'vertrag' : ['Videocall', 'Angebot verschickt'].includes(s) ? 'angebot' : 'antwort');
      setVertrag(vertragVorbelegen(d.anfrage));
      if (!d.anfrage.datum) setVorlage('rueckfrage');
    }).catch((e) => setFehler(e.response?.data?.error || 'Anfrage konnte nicht geladen werden.'));
  }, [id]);

  useEffect(() => { if (a) setAntwort(antwortMail(vorlage, a)); }, [vorlage, a?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (a) setAngebot(angebotMail({ ...a, betrag })); }, [a?.id, betrag]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (a) setVMail(vertragMail(a)); }, [a?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (a) setDMail(dankeMail(a)); }, [a?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const zeige = (t) => { setMeldung(t); setTimeout(() => setMeldung(''), 2500); };
  const aktion = async (key, fn, erfolg) => {
    setBusy(key); setFehler('');
    try {
      const d = await fn(); if (d?.anfrage) laden(d); if (erfolg) zeige(erfolg);
      if (d?.buchung) setBuchung(d.buchung);
    }
    catch (e) { setFehler(e.response?.data?.error || 'Das hat nicht geklappt.'); }
    finally { setBusy(''); }
  };
  const setStatus = (s) => aktion('status', () => anfrageService.update(id, { status: s }), `Status: ${s}`);

  const gage = Number(vertrag?.gage) || 0;
  const anzahlung = useMemo(() => Math.round(gage * 30) / 100, [gage]);

  // Vertrag wird im Browser erzeugt (kein Server-Rendering nötig)
  const pdf = async (speichern) => {
    setBusy(speichern ? 'vertrag-speichern' : 'vertrag-vorschau'); setFehler('');
    try {
      const daten = { ...vertrag, gage: vertrag.gage === '' ? null : Number(vertrag.gage), zusatzstundePreis: vertrag.zusatzstundePreis === '' ? null : Number(vertrag.zusatzstundePreis) };
      let modul;
      try { modul = await import('../contract/vertragPdf'); }
      catch (e) {
        // Nach einem neuen Deploy kennt ein offener Tab die alten Dateinamen nicht mehr → einmal neu laden
        if (/Loading chunk|ChunkLoadError|dynamically imported module/i.test(String(e && (e.message || e.name)))) {
          if (!sessionStorage.getItem('hb_chunk_reload')) { sessionStorage.setItem('hb_chunk_reload', '1'); window.location.reload(); return; }
        }
        throw e;
      }
      sessionStorage.removeItem('hb_chunk_reload');
      const { vertragAlsPdf } = modul;
      const blob = await vertragAlsPdf(daten);
      const name = `Booking-Vertrag_${(daten.veranstaltung?.datum || '').replace(/\./g, '-')}_${(daten.auftraggeber?.name || 'Kunde').replace(/[^\wäöüÄÖÜß-]+/g, '-')}.pdf`;
      const url = URL.createObjectURL(blob);
      if (speichern) {
        // herunterladen (zum Anhängen an die Mail) …
        const link = document.createElement('a'); link.href = url; link.download = name; document.body.appendChild(link); link.click(); link.remove();
        // … und an der Anfrage speichern
        await anfrageService.upload(id, 'vertrag', new File([blob], name, { type: 'application/pdf' }), false);
        const d = await anfrageService.update(id, { status: 'Vertrag verschickt', vertragsdaten: JSON.stringify(daten), ...(daten.gage != null ? { betrag: daten.gage } : {}) });
        laden(d); zeige('Vertrag gespeichert und heruntergeladen – jetzt mit der Mail verschicken');
      } else {
        if (vorschau) URL.revokeObjectURL(vorschau);
        setVorschau(url);
      }
    } catch (e) {
      console.error(e);
      setFehler('Vertrag konnte nicht erstellt werden: ' + (e.response?.data?.error || e.message || 'unbekannter Fehler'));
    } finally { setBusy(''); }
  };

  if (fehler && !a) return <div className="card text-error">{fehler}</div>;
  if (!a || !vertrag) return <div className="spinner"></div>;

  const setV = (pfad, wert) => {
    const [k1, k2] = pfad.split('.');
    setVertrag(v => k2 ? { ...v, [k1]: { ...v[k1], [k2]: wert } } : { ...v, [k1]: wert });
  };
  const feld = (label, pfad, props = {}) => {
    const [k1, k2] = pfad.split('.');
    const wert = k2 ? vertrag[k1]?.[k2] ?? '' : vertrag[k1] ?? '';
    return (
      <div className="form-group">
        <label>{label}</label>
        {props.textarea
          ? <textarea className="form-textarea" rows={3} value={wert} onChange={(e) => setV(pfad, e.target.value)} />
          : <input className="form-input" type={props.type || 'text'} value={wert} placeholder={props.placeholder} onChange={(e) => setV(pfad, e.target.value)} />}
      </div>
    );
  };

  const utm = Object.entries(a.utm || {}).filter(([, v]) => v);

  return (
    <div className="anfrage-detail">
      <Link to="/anfragen" className="back-button"><FaArrowLeft /> Alle Anfragen</Link>
      <div className="anfrage-kopf">
        <div>
          <h1>{a.vorname} {a.nachname}</h1>
          <p className="text-gray">{SERVICE_LABEL[a.service] || a.service}{a.eventart ? ` · ${a.eventart}` : ''}{a.datum ? ` · ${datumLang(a.datum)}` : ''}</p>
        </div>
        <span className={`status-badge status-${statusKlasse(a.status)}`}>{a.status}</span>
      </div>

      {meldung && <div className="meldung">{meldung}</div>}
      {buchung && (buchung.fehler
        ? <div className="card mb-3 text-error">{buchung.fehler} Bitte die Buchung kurz von Hand anlegen.</div>
        : <div className="card mb-3 buchung-hinweis">
            {buchung.neu ? 'Gebucht! Die Buchung wurde angelegt und steht jetzt im Kalender.' : 'Diese Buchung gibt es schon – sie steht im Kalender.'}{' '}
            <Link to={`/${buchung.typ === 'livemusik' ? 'livemusik' : 'dj'}/${buchung.id}`}>Zur {buchung.typ === 'livemusik' ? 'Livemusik' : 'DJ'}-Buchung →</Link>
          </div>)}
      {fehler && <div className="card mb-3 text-error">{fehler}</div>}

      <div className="pipeline">
        {statusListe.filter(s => s !== 'Abgesagt').map((s, i) => {
          const idx = statusListe.indexOf(a.status);
          return (
            <button key={s} disabled={busy === 'status'} onClick={() => setStatus(s)}
              className={`pipeline-schritt ${a.status === s ? 'aktiv' : ''} ${a.status !== 'Abgesagt' && i < idx ? 'erledigt' : ''}`}>
              <span className="nr">{i + 1}</span>{s}
            </button>
          );
        })}
        <button className={`pipeline-schritt abgesagt ${a.status === 'Abgesagt' ? 'aktiv' : ''}`} onClick={() => setStatus('Abgesagt')}>Abgesagt</button>
      </div>

      <div className="anfrage-grid">
        {/* Linke Spalte: Daten */}
        <div className="anfrage-daten">
          <div className="card">
            <h2>Anfrage</h2>
            <div className="info-zeile"><FaEnvelope /><a href={`mailto:${a.email}`}>{a.email || '–'}</a></div>
            <div className="info-zeile"><FaPhone />{a.telefon ? <a href={`tel:${a.telefon}`}>{a.telefon}</a> : '–'}</div>
            <div className="info-zeile"><FaCalendar />{datumLang(a.datum) || '–'}</div>
            <div className="info-zeile"><FaMapMarkerAlt />{a.ort || '–'}</div>
            <div className="info-zeile"><FaUsers />{a.gaeste ? `ca. ${a.gaeste} Gäste` : '–'}</div>
            {a.nachricht && <div className="nachricht">{a.nachricht}</div>}
            <dl className="meta">
              {a.quelle && <><dt>Aufmerksam durch</dt><dd>{a.quelle}</dd></>}
              {utm.map(([k, v]) => <React.Fragment key={k}><dt>UTM {k}</dt><dd>{v}</dd></React.Fragment>)}
              {a.fbclid && <><dt>Facebook-Klick</dt><dd>ja</dd></>}
              {a.gclid && <><dt>Google-Klick</dt><dd>ja</dd></>}
              {a.eingegangen && <><dt>Eingegangen</dt><dd>{new Date(a.eingegangen).toLocaleString('de-DE')}</dd></>}
            </dl>
          </div>
          <div className="card">
            <h2>Notizen</h2>
            <textarea className="form-textarea" rows={5} value={notizen} placeholder="z. B. Videocall am …, Wünsche, Absprachen" onChange={(e) => setNotizen(e.target.value)} />
            <button className="btn btn-secondary mt-2" disabled={busy === 'notizen'} onClick={() => aktion('notizen', () => anfrageService.update(id, { notizen }), 'Notizen gespeichert')}>Speichern</button>
          </div>
        </div>

        {/* Rechte Spalte: Arbeitsschritte */}
        <div className="card anfrage-arbeit">
          <div className="arbeit-tabs">
            <button className={tab === 'antwort' ? 'aktiv' : ''} onClick={() => setTab('antwort')}>1 · Antworten</button>
            <button className={tab === 'angebot' ? 'aktiv' : ''} onClick={() => setTab('angebot')}>2 · Angebot</button>
            <button className={tab === 'vertrag' ? 'aktiv' : ''} onClick={() => setTab('vertrag')}>3 · Vertrag</button>
            <button className={tab === 'danke' ? 'aktiv' : ''} onClick={() => setTab('danke')}>4 · Danke</button>
          </div>

          {tab === 'antwort' && (
            <div>
              <div className="vorlagen">
                {ANTWORT_VORLAGEN.map(v => <button key={v.id} className={`chip ${vorlage === v.id ? 'aktiv' : ''}`} onClick={() => setVorlage(v.id)}>{v.label}</button>)}
              </div>
              <MailBox mail={antwort} setMail={setAntwort} an={a.email} hinweis="Text anpassen, kopieren und aus deinem Postfach senden." />
              <div className="naechster-schritt">
                <span>Gesendet?</span>
                {vorlage === 'videocall' && <button className="btn btn-outline" onClick={() => setStatus('Videocall')}>Videocall vereinbart</button>}
                {vorlage === 'belegt'
                  ? <button className="btn btn-outline" onClick={() => setStatus('Abgesagt')}>Als abgesagt markieren</button>
                  : <button className="btn btn-outline" onClick={() => setStatus('Beantwortet')}>Als beantwortet markieren</button>}
              </div>
            </div>
          )}

          {tab === 'angebot' && (
            <div>
              <p className="text-gray mb-2">Angebot in Lexware erstellen, als PDF speichern und hier hochladen. Danach Mail kopieren und das PDF im Mailprogramm anhängen.</p>
              <div className="form-row">
                <div className="form-group">
                  <label>Angebotsbetrag (brutto, €)</label>
                  <input className="form-input" type="number" step="0.01" value={betrag} onChange={(e) => setBetrag(e.target.value)} />
                </div>
                <div className="form-group bottom">
                  <button className="btn btn-secondary" disabled={busy === 'betrag'} onClick={() => aktion('betrag', () => anfrageService.update(id, { betrag }), 'Betrag gespeichert')}>Betrag speichern</button>
                </div>
              </div>
              <Dateien liste={a.angebot} />
              <div className="button-row mb-3">
                <UploadButton label="Angebot (PDF) hochladen" busy={busy === 'angebot'} onFile={(f) => aktion('angebot', () => anfrageService.upload(id, 'angebot', f), 'Angebot gespeichert')} />
              </div>
              <MailBox mail={angebot} setMail={setAngebot} an={a.email} hinweis="Angebots-PDF im Mailprogramm anhängen." />
              <div className="naechster-schritt">
                <span>Zusage erhalten?</span>
                <button className="btn btn-primary" onClick={() => { setStatus('Angenommen'); setTab('vertrag'); setVertrag(v => ({ ...v, gage: v.gage || betrag })); }}>Angebot angenommen → Vertrag</button>
              </div>
            </div>
          )}

          {tab === 'vertrag' && (
            <div>
              <div className="vorlagen mb-2">
                <button className={`chip ${vertrag.typ === 'dj' ? 'aktiv' : ''}`} onClick={() => setV('typ', 'dj')}>DJ-Vertrag</button>
                <button className={`chip ${vertrag.typ === 'livemusik' ? 'aktiv' : ''}`} onClick={() => setV('typ', 'livemusik')}>Livemusik-Vertrag</button>
              </div>
              <h3>Auftraggeber</h3>
              <div className="form-row">{feld('Vorname, Name', 'auftraggeber.name')}{feld('Firma (optional)', 'auftraggeber.firma')}</div>
              <div className="form-row">{feld('Straße, Nr.', 'auftraggeber.strasse')}{feld('PLZ, Ort', 'auftraggeber.plzOrt')}</div>
              <div className="form-row">{feld('Telefon', 'auftraggeber.telefon')}{feld('E-Mail', 'auftraggeber.email')}</div>
              <h3>Veranstaltung</h3>
              <div className="form-row">{feld('Veranstaltungsart', 'veranstaltung.art')}{feld('Datum', 'veranstaltung.datum')}</div>
              <div className="form-row">{feld('Ort (Location)', 'veranstaltung.ort')}{feld('Anschrift', 'veranstaltung.anschrift')}</div>
              <div className="form-row">{feld('Gäste', 'veranstaltung.gaeste')}{feld(vertrag.typ === 'livemusik' ? 'Spielzeit (von/bis)' : 'Musik von/bis', 'veranstaltung.spielzeit', { placeholder: 'z. B. 19:00 Uhr bis 01:00 Uhr' })}</div>
              {feld('Aufbau der Technik (Datum, Uhrzeit von/bis)', 'veranstaltung.aufbau')}
              <h3>Inhalt & Kosten</h3>
              {feld('Bereitgestellte Technik', 'equipment', { textarea: true })}
              {feld('Wünsche des Auftraggebers', 'wuensche', { textarea: true })}
              <div className="form-row">
                {feld('Gage gesamt (brutto, €)', 'gage', { type: 'number' })}
                {feld('Zusatzstunde (€, leer = nach Absprache)', 'zusatzstundePreis', { type: 'number' })}
              </div>
              {gage > 0 && <p className="text-gray klein mb-2">Anzahlung 30 %: {anzahlung.toLocaleString('de-DE', { minimumFractionDigits: 2 })} € · Rest 70 %: {(gage - anzahlung).toLocaleString('de-DE', { minimumFractionDigits: 2 })} €</p>}
              {feld('Zusätzliche Vereinbarungen', 'zusatzvereinbarungen', { textarea: true })}

              <div className="button-row mb-3">
                <button className="btn btn-secondary" disabled={!!busy} onClick={() => pdf(false)}><FaFilePdf /> {busy === 'vertrag-vorschau' ? 'Erstelle …' : 'Vorschau'}</button>
                <button className="btn btn-primary" disabled={!!busy} onClick={() => pdf(true)}><FaFilePdf /> {busy === 'vertrag-speichern' ? 'Speichere …' : 'Vertrag erstellen & speichern'}</button>
              </div>
              {vorschau && (
                <div className="vorschau">
                  <div className="vorschau-kopf">
                    <strong>Vorschau</strong>
                    <a className="btn btn-secondary" href={vorschau} target="_blank" rel="noreferrer"><FaExternalLinkAlt /> In neuem Tab öffnen</a>
                    <button className="btn btn-secondary" onClick={() => setVorschau('')}>Schließen</button>
                  </div>
                  <iframe title="Vertragsvorschau" src={vorschau} />
                </div>
              )}
              <Dateien liste={a.vertrag} />

              <h3 className="mt-3">Mail zum Vertrag</h3>
              <MailBox mail={vMail} setMail={setVMail} an={a.email} hinweis="Vertrags-PDF im Mailprogramm anhängen." />

              <div className="naechster-schritt">
                <span>Unterschrieben zurück?</span>
                <UploadButton label="Unterschriebenen Vertrag hochladen → Gebucht" busy={busy === 'unterschrieben'} onFile={(f) => aktion('unterschrieben', () => anfrageService.upload(id, 'unterschrieben', f), 'Gebucht!')} />
              </div>
              <Dateien liste={a.unterschrieben} />
            </div>
          )}

          {tab === 'danke' && (
            <div>
              <p className="text-gray mb-2">Ein, zwei Tage nach dem Event schicken. Die Mail enthält den Link zu deinen Google-Rezensionen.</p>
              <MailBox mail={dMail} setMail={setDMail} an={a.email} hinweis="Text anpassen, kopieren und aus deinem Postfach senden." />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AnfrageDetail;
